import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { m } from 'motion/react';
import { ArrowRight, Building2, ChevronRight, Clock, FileCheck2, Layers3, LockKeyhole, MailWarning, ShieldCheck, ShieldHalf } from 'lucide-react';
import { BRAND, PLATFORM_BRAND, type MeResponse, type PublicBrandingDTO } from '@zuri/shared';
import { api, ApiError } from '../../lib/api';
import { cx } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Button } from '../../components/ui/Button';
import { Field, PasswordInput, TextInput } from '../../components/ui/Field';
import { InlineAlert } from '../../components/ui/States';
import { Badge } from '../../components/ui/Badge';
import { CompanyAvatar, CompanyLogo, PlatformMark, useBrandTheme } from '../../components/Brand';

const EXPIRED_FLAG = 'zuri-session-expired';

/** Identidade pública de uma empresa (somente nome e marca), resolvida pelo servidor a partir do endereço. */
function usePublicBranding(slug: string | null | undefined) {
  return useQuery({
    queryKey: ['public-branding', slug],
    queryFn: () => api<PublicBrandingDTO>(`/public/branding/${encodeURIComponent(slug!)}`),
    enabled: !!slug,
    retry: false,
    staleTime: 300_000,
  });
}

const fieldError = (e: unknown, key: string) => (e instanceof ApiError ? e.fields?.[key] : undefined);

/** Entrada curta e única (não se repete a cada atualização). */
const enter = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.32, ease: [0.2, 0.8, 0.2, 1] } } as const;

function Highlights({ company }: { company: boolean }) {
  const items = [
    { icon: <ShieldCheck />, text: 'Aprovações com responsável, horário e justificativa registrados.' },
    { icon: <FileCheck2 />, text: 'Baixas conferidas com comprovante e saldo calculado dos registros.' },
    { icon: <Layers3 />, text: company ? 'Dados, usuários e permissões exclusivos da sua empresa.' : 'Cada empresa com dados, usuários e permissões isolados.' },
  ];
  return (
    <ul className="space-y-3.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-[14px] leading-relaxed text-white/80">
          <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-[10px] bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] [&_svg]:size-4" aria-hidden>{it.icon}</span>
          {it.text}
        </li>
      ))}
    </ul>
  );
}

/**
 * Moldura das telas de acesso. Desktop: área institucional sóbria + área de acesso.
 * Celular: coluna única, logotipo proporcional e formulário logo no início.
 */
function AuthFrame({ title, subtitle, children, footer, brand, split = false, eyebrow }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; brand: PublicBrandingDTO | null; split?: boolean; eyebrow?: ReactNode }) {
  useBrandTheme(brand, brand ? `Acesso · ${brand.name}` : 'Acesso');
  const deep = brand?.primaryColor ?? PLATFORM_BRAND.primary;
  const identity = brand ? (
    <CompanyLogo branding={brand} name={brand.name} height={34} />
  ) : (
    <div className="flex items-center gap-2.5">
      <PlatformMark className="size-9" />
      <span className="text-[17px] font-[650] tracking-[-0.015em]">{BRAND.name}</span>
    </div>
  );
  return (
    <div className={cx('relative min-h-dvh', split && 'lg:grid lg:grid-cols-[minmax(440px,44%)_1fr]')}>
      <div className="app-backdrop" aria-hidden />
      {split && (
        <aside className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14" style={{ background: deep }} aria-label="Apresentação">
          {/* Profundidade discreta: brilhos estáticos com as cores de apoio da marca. */}
          <div className="pointer-events-none absolute inset-0" aria-hidden style={{ background: `radial-gradient(620px 420px at 0% 0%, rgba(255,255,255,0.10), transparent 70%), radial-gradient(560px 460px at 110% 105%, ${brand?.accentColor ?? '#3B7BF6'}33, transparent 70%), radial-gradient(420px 300px at 90% 10%, ${brand?.highlightColor ?? '#3B7BF6'}1f, transparent 70%)` }} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,1)_1px,transparent_1px)] [background-size:48px_48px]" aria-hidden />
          <div className="relative">
            {brand ? (
              <CompanyLogo branding={brand} name={brand.name} height={44} onDark />
            ) : (
              <div className="flex items-center gap-3 text-white">
                <svg viewBox="0 0 64 64" className="size-11" aria-hidden><rect width="64" height="64" rx="15" fill="rgba(255,255,255,0.12)" /><path d="M20 21h24L22 43h22" fill="none" stroke="#fff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                <span className="text-[20px] font-[650] tracking-[-0.02em]">{BRAND.name}</span>
              </div>
            )}
          </div>
          <div className="relative max-w-[440px]">
            <h2 className={cx('text-[34px] leading-[1.12] tracking-[-0.025em] text-white', brand ? 'font-display font-[600]' : 'font-[680]')}>
              {brand ? 'Gestão financeira com clareza e controle.' : 'Financeiro organizado, preciso e auditável.'}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              {brand ? `Ambiente financeiro exclusivo da ${brand.name}.` : BRAND.tagline + ' — contas a pagar e a receber, aprovações e bancos em um só lugar.'}
            </p>
            <div className="mt-9"><Highlights company={!!brand} /></div>
          </div>
          <div className="relative flex items-center gap-2 text-[12.5px] text-white/60">
            <LockKeyhole className="size-3.5" aria-hidden />
            Conexão protegida · {brand ? `operado com ${BRAND.name}` : 'dados isolados por empresa'}
          </div>
        </aside>
      )}
      <main className="relative flex min-h-dvh flex-col px-5 pb-8 pt-[max(20px,var(--safe-top))] sm:px-8 lg:items-center lg:justify-center lg:py-12">
        <m.div {...enter} className="mx-auto w-full max-w-[420px] lg:mt-0">
          <div className={cx('mb-7 flex items-center sm:mb-9', split && 'lg:hidden')}>{identity}</div>
          {eyebrow && <div className="mb-2">{eyebrow}</div>}
          <h1 className="text-[28px] font-[680] leading-tight tracking-[-0.026em] sm:text-[30px]">{title}</h1>
          {subtitle && <p className="mt-2 text-[14.5px] leading-relaxed text-ink-2">{subtitle}</p>}
          <div className="card auth-form-card mt-6 p-5 sm:p-7">{children}</div>
          {footer && <div className="mt-6 text-center text-[13.5px] text-ink-2">{footer}</div>}
          <p className="mt-10 text-center text-[12px] text-ink-3">{brand ? `${brand.name} · ${BRAND.name}` : `${BRAND.name} · ${BRAND.tagline}`}</p>
        </m.div>
      </main>
    </div>
  );
}

/** Entrada. Em /entrar/:empresa a identidade da empresa é aplicada e ela fica ativa após o login. */
export function LoginPage() {
  const { me, applySession } = useSession();
  const navigate = useNavigate();
  const { empresa } = useParams();
  const [params] = useSearchParams();
  const branding = usePublicBranding(empresa);
  const brand = branding.data ?? null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [expired] = useState(() => {
    try {
      const v = sessionStorage.getItem(EXPIRED_FLAG) === '1' || params.get('expirada') === '1';
      sessionStorage.removeItem(EXPIRED_FLAG);
      return v;
    } catch {
      return params.get('expirada') === '1';
    }
  });
  const passwordRef = useRef<HTMLInputElement>(null);
  // Retorno à página de origem (somente caminhos internos).
  const rawNext = params.get('next');
  const nextPath = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/';
  const m = useMutation({
    mutationFn: () => api('/auth/login', { body: { email, password, tenantSlug: brand?.slug ?? null } }),
    onSuccess: async () => {
      applySession(await api<MeResponse>('/auth/me'));
      navigate(nextPath, { replace: true });
    },
    onError: () => {
      setPassword('');
      passwordRef.current?.focus();
    },
  });
  if (me) return <Navigate to={nextPath} replace />;
  if (empresa && branding.isError) return <Navigate to="/entrar" replace />;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!m.isPending) m.mutate();
  };
  return (
    <AuthFrame
      split
      brand={brand}
      eyebrow={brand ? <Badge tone="neutral" icon={<Building2 className="size-3.5" aria-hidden />}>Acesso da empresa</Badge> : undefined}
      title="Seu financeiro, em dia."
      subtitle={brand ? `Use seu e-mail de acesso à ${brand.name}.` : 'Acesse a gestão financeira da sua empresa.'}
      footer={!brand && <>Ainda não tem conta? <Link to="/cadastro" className="font-[560] text-primary-text hover:underline">Cadastre sua empresa</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" noValidate aria-describedby={m.error ? 'login-error' : undefined}>
        {expired && !m.error && <InlineAlert tone="blue" icon={<Clock className="size-4" />}>Sua sessão expirou. Entre novamente para continuar.</InlineAlert>}
        {m.error && <div id="login-error"><InlineAlert tone="red">{(m.error as Error).message}</InlineAlert></div>}
        <Field label="E-mail">{(p) => <TextInput {...p} type="email" name="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
        <div>
          <Field label="Senha">
            {(p) => <PasswordInput {...p} ref={passwordRef} name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}
          </Field>
          <div className="mt-2 flex justify-end">
            <Link to={`/esqueci-senha${brand ? `?empresa=${brand.slug}` : ''}`} className="inline-flex min-h-8 items-center text-[13px] font-[560] text-primary-text hover:underline coarse:min-h-10">Esqueci a senha</Link>
          </div>
        </div>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={m.isPending} disabled={!email || !password}>
          Entrar
          {!m.isPending && <ArrowRight aria-hidden />}
        </Button>
      </form>
    </AuthFrame>
  );
}

export function SignupPage() {
  const { me, applySession } = useSession();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '', document: '' });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const mut = useMutation({
    mutationFn: () => api('/auth/signup', { body: { name: form.name, email: form.email, password: form.password, company: { name: form.company, document: form.document || null } } }),
    onSuccess: async () => {
      applySession(await api<MeResponse>('/auth/me'));
      navigate('/app');
    },
  });
  if (me) return <Navigate to="/" replace />;
  return (
    <AuthFrame brand={null} title="Criar conta" subtitle="Cadastre sua empresa e comece pelo período de avaliação." footer={<>Já tem conta? <Link to="/entrar" className="font-[560] text-primary-text hover:underline">Entrar</Link></>}>
      <form onSubmit={(e) => { e.preventDefault(); if (!mut.isPending) mut.mutate(); }} className="space-y-4" noValidate>
        {mut.error && <InlineAlert tone="red">{(mut.error as Error).message}</InlineAlert>}
        <fieldset className="space-y-4">
          <legend className="eyebrow mb-1">Empresa</legend>
          <Field label="Nome da empresa" required error={fieldError(mut.error, 'company.name')}>{(p) => <TextInput {...p} autoComplete="organization" value={form.company} onChange={set('company')} />}</Field>
          <Field label="CNPJ" hint="Opcional">{(p) => <TextInput {...p} inputMode="numeric" value={form.document} onChange={set('document')} />}</Field>
        </fieldset>
        <div className="h-px bg-separator" />
        <fieldset className="space-y-4">
          <legend className="eyebrow mb-1">Seu acesso</legend>
          <Field label="Seu nome" required error={fieldError(mut.error, 'name')}>{(p) => <TextInput {...p} autoComplete="name" value={form.name} onChange={set('name')} />}</Field>
          <Field label="E-mail" required error={fieldError(mut.error, 'email')}>{(p) => <TextInput {...p} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={form.email} onChange={set('email')} />}</Field>
          <Field label="Senha" required hint="Mínimo de 10 caracteres, com letras e números." error={fieldError(mut.error, 'password')}>{(p) => <PasswordInput {...p} autoComplete="new-password" value={form.password} onChange={set('password')} />}</Field>
        </fieldset>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={mut.isPending}>
          Criar empresa
        </Button>
        <p className="text-center text-[12px] text-ink-3">Você será o administrador desta empresa. Outros usuários são convidados nas configurações.</p>
      </form>
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const branding = usePublicBranding(params.get('empresa'));
  const [email, setEmail] = useState('');
  const mut = useMutation({ mutationFn: () => api<{ sent: boolean; emailConfigured: boolean; message: string }>('/auth/password/forgot', { body: { email } }) });
  const back = branding.data ? `/entrar/${branding.data.slug}` : '/entrar';
  return (
    <AuthFrame brand={branding.data ?? null} title="Recuperar acesso" subtitle="Enviaremos um link de redefinição para o seu e-mail." footer={<Link to={back} className="font-[560] text-primary-text hover:underline">Voltar para o login</Link>}>
      {mut.data ? (
        mut.data.emailConfigured ? (
          <InlineAlert tone="green" title="Verifique seu e-mail">{mut.data.message}</InlineAlert>
        ) : (
          <InlineAlert tone="orange" icon={<MailWarning className="size-4" />} title="E-mail não configurado neste ambiente">
            {mut.data.message}
          </InlineAlert>
        )
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (!mut.isPending) mut.mutate(); }} className="space-y-4" noValidate>
          {mut.error && <InlineAlert tone="red">{(mut.error as Error).message}</InlineAlert>}
          <Field label="E-mail">{(p) => <TextInput {...p} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={mut.isPending} disabled={!email}>
            Enviar link
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}

/** Primeiro acesso / redefinição por link de uso único. */
export function AccessTokenPage() {
  const { token = '' } = useParams();
  const { applySession } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const info = useQuery({ queryKey: ['token', token], queryFn: () => api<{ purpose: string; email: string; name: string }>(`/auth/token/${encodeURIComponent(token)}`), retry: false });
  const mut = useMutation({
    mutationFn: () => api('/auth/password/reset', { body: { token, password } }),
    onSuccess: async () => {
      applySession(await api<MeResponse>('/auth/me'));
      navigate('/');
    },
  });
  if (info.isError) {
    return (
      <AuthFrame brand={null} title="Link inválido" footer={<Link to="/entrar" className="font-[560] text-primary-text hover:underline">Ir para o login</Link>}>
        <InlineAlert tone="red">{(info.error as Error).message}</InlineAlert>
      </AuthFrame>
    );
  }
  const setup = info.data?.purpose === 'account_setup';
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <AuthFrame brand={null} title={setup ? 'Defina sua senha' : 'Nova senha'} subtitle={info.data ? `${info.data.name} · ${info.data.email}` : 'Validando link…'}>
      <form onSubmit={(e) => { e.preventDefault(); if (!mismatch && !mut.isPending) mut.mutate(); }} className="space-y-4" noValidate>
        {mut.error && <InlineAlert tone="red">{(mut.error as Error).message}</InlineAlert>}
        {/* Campo oculto de usuário: ajuda gerenciadores de senha a associar a credencial. */}
        <input type="email" name="username" autoComplete="username" value={info.data?.email ?? ''} readOnly hidden />
        <Field label="Senha" hint="Mínimo de 10 caracteres, com letras e números." error={fieldError(mut.error, 'password')}>{(p) => <PasswordInput {...p} autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        <Field label="Confirme a senha" error={mismatch ? 'As senhas não coincidem' : null}>{(p) => <PasswordInput {...p} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={mut.isPending} disabled={!info.data || !password || mismatch}>
          Salvar e entrar
        </Button>
      </form>
    </AuthFrame>
  );
}

/** Seleção de empresa para quem tem vários vínculos. A troca é validada no servidor. */
export function SelectCompanyPage() {
  const { me, switchCompany, logout } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    if (me?.memberships.length === 1 && !me.platformRole && !busy) {
      setBusy(me.memberships[0].tenantId);
      void switchCompany(me.memberships[0].tenantId).then(() => navigate('/app'));
    }
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!me) return <Navigate to="/entrar" replace />;
  return (
    <AuthFrame brand={null} title="Escolha a empresa" subtitle={`Olá, ${me.user.name.split(' ')[0]}. Você participa de ${me.memberships.length} ${me.memberships.length === 1 ? 'empresa' : 'empresas'}.`} footer={<button type="button" className="font-[560] text-ink-2 hover:text-ink" onClick={() => void logout()}>Sair</button>}>
      <div className="-m-2 space-y-1">
        {me.memberships.map((mm) => (
          <button
            key={mm.tenantId}
            type="button"
            disabled={!!busy}
            onClick={async () => {
              setBusy(mm.tenantId);
              try {
                await switchCompany(mm.tenantId);
                navigate('/app');
              } finally {
                setBusy(null);
              }
            }}
            className="flex min-h-14 w-full items-center gap-3 rounded-[14px] p-3 text-left transition-colors hover:bg-hover disabled:opacity-60"
          >
            <CompanyAvatar name={mm.tenantName} src={mm.logoUrl} size={40} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-[600]">{mm.tenantName}</div>
              <div className="truncate text-[12.5px] text-ink-3">{mm.roleName}</div>
            </div>
            {mm.isDemo && <Badge tone="violet">Demo</Badge>}
            <ChevronRight className="size-4 text-ink-3" aria-hidden />
          </button>
        ))}
        {me.memberships.length === 0 && <p className="p-3 text-center text-[13px] text-ink-2"><Building2 className="mx-auto mb-2 size-6 text-ink-3" aria-hidden />Nenhuma empresa vinculada.</p>}
        {me.platformRole && (
          <>
            <div className="mx-3 my-2 h-px bg-separator" />
            <button type="button" onClick={() => navigate('/admin')} className="flex min-h-14 w-full items-center gap-3 rounded-[14px] p-3 text-left transition-colors hover:bg-hover">
              <span className="grid size-10 place-items-center rounded-[10px] bg-sunken text-ink-2" aria-hidden><ShieldHalf className="size-5" /></span>
              <div className="min-w-0 flex-1 font-[600]">Administração da plataforma</div>
              <ChevronRight className="size-4 text-ink-3" aria-hidden />
            </button>
          </>
        )}
      </div>
    </AuthFrame>
  );
}

export { EXPIRED_FLAG };
